import { IColumn, mergeStyleSets } from "@bps/fluent-ui";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { CoreStore } from "@stores/core/CoreStore.ts";
import { RootStore } from "@stores/root/RootStore.ts";

import {
  AppointmentCancellationReason,
  AppointmentInformationApptType,
  AppointmentInformationClaim,
  AppointmentInformationDate,
  AppointmentInformationDuration,
  AppointmentInformationLocation,
  AppointmentInformationProvider,
  AppointmentInformationStatus
} from "./AppointmentInformationTableCells.tsx";

interface AppointmentInformationTableColumnsArgs {
  core: CoreStore;
  claimId: string | undefined;
  onDismiss: () => void;
}

export const getAppointmentInformationTableColumns = ({
  core,
  claimId,
  onDismiss
}: AppointmentInformationTableColumnsArgs) => {
  const columns: IColumn[] = [
    {
      key: "date",
      isRowHeader: true,
      minWidth: 90,
      maxWidth: 100,
      name: "Date",
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationDate
          calendarEvent={calendarEvent}
          onDismiss={onDismiss}
        />
      ),
      isMultiline: true
    },
    {
      key: "status",
      minWidth: 80,
      maxWidth: 90,
      name: "Status",
      isMultiline: true,
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationStatus calendarEvent={calendarEvent} />
      )
    },
    {
      key: "type",
      minWidth: 130,
      maxWidth: 230,
      name: "Appointment type",
      isMultiline: true,
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationApptType calendarEvent={calendarEvent} />
      )
    },
    {
      key: "duration",
      minWidth: 70,
      maxWidth: 90,
      name: "Duration",
      isMultiline: true,
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationDuration calendarEvent={calendarEvent} />
      ),
      styles: mergeStyleSets({
        cellTitle: {
          justifyContent: "flex-end"
        }
      })
    },
    {
      key: "provider",
      minWidth: 100,
      maxWidth: 150,
      name: "Provider",
      isMultiline: true,
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationProvider calendarEvent={calendarEvent} />
      )
    }
  ];

  if (core.hasMultipleActiveLocations) {
    columns.push({
      key: "location",
      minWidth: 100,
      maxWidth: 150,
      name: "Location",
      isMultiline: true,
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationLocation calendarEvent={calendarEvent} />
      )
    });
  }

  if (core.isNZTenant && !claimId) {
    columns.push({
      key: "claim",
      minWidth: 80,
      maxWidth: 80,
      name: "Claim no.",
      onRender: (calendarEvent: CalendarEvent) => (
        <AppointmentInformationClaim calendarEvent={calendarEvent} />
      )
    });
  }

  columns.push({
    key: "cancellationReason",
    minWidth: 250,
    maxWidth: 300,
    name: "Reason",
    isMultiline: true,
    onRender: (calendarEvent: CalendarEvent) => (
      <AppointmentCancellationReason calendarEvent={calendarEvent} />
    )
  });

  return columns;
};

export const preloadClaims = async (root: RootStore, contactId: string) => {
  if (!root.core.isNZTenant) return;

  const result = await root.acc.fetchClaims({ patients: [contactId] });

  await root.acc.getClaimEpisodesOfCare({
    claimIds: result.results.map(claim => claim.id)
  });
};
